import React, { ReactNode, useCallback } from 'react'
import type { FieldError as HookFieldError } from 'react-hook-form'

import { ColorPicker } from '@/components/ui/color-picker'
import { cn } from '@/lib/utils'
import { IconTypes } from '@/components/Icon'
import FieldError from '@/components/livecontact/FieldError'
import {
  FOCUS_RING,
  INPUT_ERROR_CLASS,
  LABEL_CLASS,
  NOTE_CLASS,
} from '@/components/livecontact/fieldStyles'

export type Props = {
  label?: string
  value: string
  note?: string
  onChange: (value: any) => void
  onBlur?: () => void
  className?: string
  classLabel?: string
  classInput?: string
  classPicker?: string
  classInner?: string
  classNote?: string
  error?: HookFieldError
  disabled?: boolean
  icon?: IconTypes
  placeholder?: string
  required?: boolean
  appendComponent?: () => ReactNode
  visibleInput?: boolean
  /** Quick swatches inside the picker popover. */
  presets?: string[]
}

/** Colour chip: 32px swatch button (hairline ring, popover picker) + hex text on a 40px field surface. */
const ColorField: React.FC<Props> = ({
  label,
  value,
  onChange,
  onBlur,
  note,
  className,
  classLabel,
  classPicker,
  classInput,
  classInner,
  classNote,
  error,
  disabled,
  placeholder = '#000000',
  required,
  appendComponent = () => null,
  visibleInput = true,
  presets,
}) => {
  // Both the swatch and the text field hand back a plain string, so consumers never branch on events.
  const handleChange = useCallback((next: string) => onChange(next), [onChange])

  return (
    <div className={cn('flex flex-col', className)}>
      {label && <label className={cn(LABEL_CLASS, classLabel)}>{label}</label>}
      <div
        className={cn(
          'relative inline-flex items-center gap-2 rounded-[10px] bg-surface p-1 shadow-input',
          'transition-shadow duration-[160ms] ease-out-strong',
          `focus-within:shadow-[${FOCUS_RING}]`,
          visibleInput ? 'w-fit pr-3' : 'w-full',
          error && INPUT_ERROR_CLASS,
          classInner
        )}
      >
        {appendComponent()}
        {/* Only the controls dim when disabled, so an appended gate (premium veil) stays crisp on top. */}
        <span className={cn('contents', disabled && '[&>*]:opacity-60')}>
          <ColorPicker
            value={value}
            onChange={handleChange}
            onBlur={onBlur}
            className={cn('h-8 w-8 rounded-[7px]', !visibleInput && 'w-full', classPicker)}
            disabled={disabled}
            presets={presets}
          />
          {visibleInput && (
            <input
              className={cn(
                'h-8 w-[88px] bg-transparent text-[14px] font-medium uppercase tracking-[-0.2px] text-ink outline-none tabular-nums placeholder:normal-case placeholder:text-ink-4 disabled:cursor-not-allowed',
                classInput
              )}
              type="text"
              value={value}
              onChange={(e) => handleChange(e.target.value)}
              onBlur={onBlur}
              placeholder={placeholder}
              required={required}
              disabled={disabled}
              maxLength={7}
              spellCheck={false}
              autoComplete="off"
              aria-invalid={!!error}
              aria-label={label ?? 'Colour'}
            />
          )}
        </span>
      </div>

      {note && <p className={cn(NOTE_CLASS, classNote)}>{note}</p>}
      <FieldError message={error?.message} />
    </div>
  )
}

export default ColorField
